import React, { useEffect, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import srLocale from '@fullcalendar/core/locales/sr';
import API from '../api';
import AppointmentGroupModal from './AppointmentGroupModal';

const CalendarView = () => {
    const [rawAppointments, setRawAppointments] = useState([]);
    const [allAppointments, setAllAppointments] = useState([]);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [filters, setFilters] = useState({ doctorName: '', search: '' });
    const [modalAppointments, setModalAppointments] = useState(null);

    const fetchInitialData = async () => {
        try {
            const [resApp, resDocs] = await Promise.all([
                API.get('/appointments'),
                API.get('/doctors')
            ]);

            const formatted = resApp.data.map(app => ({
                id: app.id,
                title: app.patientFullName,
                start: app.startTime,
                end: app.endTime,
                extendedProps: {
                    doctorName: app.doctorFullName,
                    phone: app.patientPhone,
                    service: app.serviceName
                },
                backgroundColor: app.serviceName?.toLowerCase().includes('mrsavljenje') ? '#10b981' : '#34a0b8',
                borderColor: 'transparent',
            }));

            setRawAppointments(resApp.data);
            setAllAppointments(formatted);
            setFilteredEvents(formatted);
            setDoctors(resDocs.data);
        } catch (err) {
            console.error("Greška pri učitavanju:", err);
        }
    };

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        let temp = allAppointments;
        if (filters.doctorName) {
            temp = temp.filter(event => event.extendedProps?.doctorName === filters.doctorName);
        }
        if (filters.search) {
            const term = filters.search.toLowerCase();
            temp = temp.filter(event =>
                (event.title?.toLowerCase() || '').includes(term) ||
                (event.extendedProps?.phone || '').includes(term)
            );
        }
        setFilteredEvents(temp);
    }, [filters, allAppointments]);

    const openGroupModal = (clickedId) => {
        const clicked = rawAppointments.find(a => a.id === Number(clickedId));
        if (!clicked) return;
        const group = rawAppointments.filter(a =>
            a.doctorFullName === clicked.doctorFullName &&
            a.startTime === clicked.startTime &&
            a.status !== 'CANCELLED'
        );
        setModalAppointments(group);
    };

    const renderEventContent = (eventInfo) => (
        <div style={styles.eventBox}>
            <div style={styles.eventTitle}>{eventInfo.event.title}</div>
            <div style={styles.eventSub}>{eventInfo.event.extendedProps.service}</div>
        </div>
    );

    return (
        <div style={styles.container}>
            {/* Naslov i Filteri - prilagođeno za uske ekrane */}
            <div style={styles.header}>
                <h2 style={styles.headerTitle}>Acu Slim</h2>
                <div style={styles.filterBar}>
                    <input 
                        type="text" 
                        placeholder="Pretraži..." 
                        style={styles.searchInput}
                        onChange={(e) => setFilters({...filters, search: e.target.value})}
                    />
                    <select 
                        style={styles.selectInput}
                        onChange={(e) => setFilters({...filters, doctorName: e.target.value})}
                    >
                        <option value="">Svi dr</option>
                        {doctors.map(d => (
                            <option key={d.id} value={d.doctorFullName || `dr ${d.firstName}`}>
                                {d.firstName}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Kalendar bez sopstvenog skrola */}
            <div style={styles.calendarCard}>
                <FullCalendar
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    initialView={window.innerWidth < 768 ? "timeGridDay" : "timeGridWeek"}
                    locale={srLocale}
                    headerToolbar={{
                        left: 'prev,next',
                        center: 'title',
                        right: 'today'
                    }}
                    events={filteredEvents}
                    eventContent={renderEventContent}
                    
                    // KLJUČ ZA MOBILNI SKROL:
                    height="auto"          // Kalendar se rasteže prema sadržaju
                    contentHeight="auto"   // Nema internog scrollbara
                    stickyHeaderDates={true} // Zaglavlje sa datumom ostaje na vrhu dok skroluješ stranu
                    
                    slotDuration="00:15:00"
                    slotLabelInterval="00:30:00"
                    slotMinTime="08:00:00"
                    slotMaxTime="21:00:00"
                    allDaySlot={false}
                    nowIndicator={true}
                    slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
                    eventClick={(info) => {
                        openGroupModal(info.event.id);
                    }}
                />
            </div>

            <AppointmentGroupModal
                appointments={modalAppointments}
                onClose={() => setModalAppointments(null)}
                onRefresh={fetchInitialData}
            />
        </div>
    );
};

const styles = {
    container: {
        padding: '10px', // Manji padding na mobilnom
        backgroundColor: '#f8fafc',
        minHeight: '100vh',
    },
    header: {
        display: 'flex',
        flexDirection: 'column', // Filteri idu jedan ispod drugog na mobilnom
        gap: '10px',
        marginBottom: '15px'
    },
    headerTitle: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#0f172a',
        margin: '0'
    },
    filterBar: {
        display: 'flex',
        gap: '8px',
        width: '100%'
    },
    searchInput: {
        flex: 2,
        padding: '12px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        fontSize: '14px',
        outline: 'none'
    },
    selectInput: {
        flex: 1,
        padding: '12px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        backgroundColor: '#fff',
        fontSize: '14px'
    },
    calendarCard: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden' // Da ivice kalendara ne vire
    },
    eventBox: {
        padding: '2px',
        lineHeight: '1.1'
    },
    eventTitle: {
        fontWeight: '600',
        fontSize: '11px',
    },
    eventSub: {
        fontSize: '9px',
        opacity: 0.8
    }
};

export default CalendarView;